// client/src/components/ProductCard.js
import React, { useContext, useState, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import { Card, CardActionArea, CardContent, CardMedia, Typography, Button, Chip, Box, IconButton, Tooltip } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { formatCurrency, getSavingsPercent } from '../utils/currency';
import { getImageUrl, API_BASE_URL } from '../utils/media';
import { LOW_STOCK_THRESHOLD } from '../constants/inventory';
import { isInCompare, toggleCompare } from '../utils/compareList';
import { useGeo } from '../context/GeoContext';

// Scrolls a too-long product name fully into view and back, on hover only —
// short names that already fit are never touched (see the overflowPx check
// where this is used).
const scrollName = keyframes`
  0%, 10% { transform: translateX(0); }
  45%, 55% { transform: translateX(var(--marquee-distance)); }
  90%, 100% { transform: translateX(0); }
`;

const ProductCard = ({ product, width, sx }) => {
  const { addToCart } = useContext(CartContext);
  const { token } = useContext(AuthContext);
  const { formatLocalPrice } = useGeo();
  const [wishlisted, setWishlisted] = useState(false);
  const [inCompare, setInCompare] = useState(() => isInCompare(product.id));
  const nameViewportRef = useRef(null);
  const nameTextRef = useRef(null);
  const [nameOverflowPx, setNameOverflowPx] = useState(0);

  useLayoutEffect(() => {
    const viewport = nameViewportRef.current;
    const text = nameTextRef.current;
    if (viewport && text) {
      setNameOverflowPx(Math.max(0, text.scrollWidth - viewport.clientWidth));
    }
  }, [product.name]);
  const quantity = Number(product.quantity);
  const inStock = quantity > 0;
  const lowStock = inStock && quantity <= LOW_STOCK_THRESHOLD;
  const hasVariants = Boolean(product.has_variants);
  const savingsPercent = getSavingsPercent(product.price, product.compare_at_price);

  const handleCompareToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { added, limitReached } = toggleCompare(product.id);
    if (limitReached) {
      alert('You can compare up to 4 products at a time. Remove one first.');
      return;
    }
    setInCompare(added);
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token) {
      alert('Please log in to add items to your wishlist.');
      return;
    }
    try {
      await fetch(`${API_BASE_URL}/api/wishlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productId: product.id }),
      });
      setWishlisted(true);
      setTimeout(() => setWishlisted(false), 2000);
    } catch (err) {
      alert('Failed to add to wishlist.');
    }
  };

  return (
    <Card sx={{ width: width || '100%', display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'relative', ...sx }}>
      <IconButton
        onClick={handleWishlist}
        aria-label="Add to wishlist"
        sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' }, zIndex: 1 }}
        size="small"
      >
        {wishlisted ? <FavoriteIcon color="error" fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
      </IconButton>
      <Tooltip title={inCompare ? 'Remove from compare' : 'Add to compare'}>
        <IconButton
          onClick={handleCompareToggle}
          aria-label="Toggle compare"
          sx={{ position: 'absolute', top: 6, right: 44, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' }, zIndex: 1 }}
          size="small"
          color={inCompare ? 'primary' : 'default'}
        >
          <CompareArrowsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <CardActionArea
        component={Link}
        to={`/products/${product.id}`}
        sx={{
          flexGrow: 1, alignItems: 'stretch',
          ...(nameOverflowPx > 0 && {
            '&:hover .product-name-text': {
              animation: `${scrollName} ${Math.min(8, Math.max(2.5, nameOverflowPx / 30))}s ease-in-out infinite`,
            },
          }),
        }}
      >
        <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: 'action.hover' }}>
          <CardMedia
            component="img"
            image={getImageUrl(product.imageUrl)}
            alt={product.name}
            sx={{
              height: 200,
              objectFit: 'contain',
              p: 1.5,
              transition: 'transform 0.4s ease',
              '.MuiCard-root:hover &': { transform: 'scale(1.05)' },
            }}
          />
          {savingsPercent && (
            <Chip label={`-${savingsPercent}%`} color="error" size="small" sx={{ position: 'absolute', top: 10, left: 10, fontWeight: 700 }} />
          )}
          {inStock && !hasVariants && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddShoppingCartIcon fontSize="small" />}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToCart(product); }}
              sx={{
                position: 'absolute', left: 12, right: 12, bottom: 12,
                opacity: { xs: 1, md: 0 }, transform: { md: 'translateY(8px)' },
                transition: 'opacity 0.2s ease, transform 0.2s ease',
                '.MuiCard-root:hover &': { opacity: 1, transform: 'none' },
              }}
            >
              Quick add
            </Button>
          )}
        </Box>
        <CardContent>
          {product.category && (
            <Chip label={product.category} size="small" sx={{ mb: 1 }} />
          )}
          <Box ref={nameViewportRef} sx={{ overflow: 'hidden', width: '100%' }}>
            <Typography
              ref={nameTextRef}
              className="product-name-text"
              variant="subtitle1"
              fontWeight={600}
              component="div"
              sx={{
                whiteSpace: 'nowrap',
                display: 'inline-block',
                maxWidth: '100%',
                ...(nameOverflowPx > 0
                  ? { '--marquee-distance': `-${nameOverflowPx}px`, transition: 'transform 0.3s ease' }
                  : { overflow: 'hidden', textOverflow: 'ellipsis' }),
              }}
            >
              {product.name}
            </Typography>
          </Box>
          {product.store_name && (
            <Typography variant="caption" color="text.secondary">Sold by {product.store_name}</Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
            <Typography variant="h6">{formatCurrency(product.price)}</Typography>
            {savingsPercent && (
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                {formatCurrency(product.compare_at_price)}
              </Typography>
            )}
          </Box>
          {formatLocalPrice(product.price) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              ≈ {formatLocalPrice(product.price)}
            </Typography>
          )}
          {lowStock && (
            <Typography variant="caption" color="error.main" fontWeight={600} sx={{ display: 'block', mt: 0.5 }}>
              Only {quantity} left in stock
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
      <Box sx={{ p: 2, pt: 0 }}>
        {!inStock ? (
          <Chip label="Out of Stock" color="default" size="small" />
        ) : hasVariants ? (
          <Button fullWidth variant="outlined" component={Link} to={`/products/${product.id}`}>
            Select Options
          </Button>
        ) : (
          <Button fullWidth variant="contained" onClick={() => addToCart(product)}>
            Add to Cart
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default ProductCard;
